import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import fidarDark from '../images/Agenti5_dark_bck.png';
import fidarLight from '../images/Agenti5_light_bck.png';
import { Icon, IconName } from './ui';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { Permission } from '../types/auth';
import { UserAvatar } from './UserAvatar';
import { ChangePasswordModal } from './ChangePasswordModal';
import { LanguageSwitcher } from './LanguageSwitcher';

interface SidebarProps {
  isConnected: boolean;
  isDarkMode: boolean;
  onToggleDarkMode: () => void;
  onLogout?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  showCollapseToggle?: boolean;
  mobileOpen?: boolean;
  onNavigate?: () => void;
}

// Each nav item can specify a permission required to see it. Items without a
// permission are visible to all authenticated users.
interface NavLinkItem {
  to: string;
  labelKey: string;
  icon: IconName;
  pocOnly?: boolean;
  permission?: Permission;
}

// A foldable section: renders as a togglable header with indented children
// instead of a direct link. Auto-expands when one of its children is active.
interface NavGroupItem {
  labelKey: string;
  icon: IconName;
  children: NavLinkItem[];
}

type NavEntry = NavLinkItem | NavGroupItem;

const isNavGroup = (item: NavEntry): item is NavGroupItem => 'children' in item;

/**
 * Navigation, ordered as the product is explained: you see where you stand,
 * find the AI you didn't know about, decide what is allowed, enforce it, watch
 * what happens, and prove it afterwards.
 *
 *   Dashboard / Command Center  — where do I stand?      (top level: the landing pages)
 *   Discover                    — what AI is out there?
 *   Access Control              — what is allowed?
 *   Enforcement                 — how is it stopped?
 *   Monitoring                  — what actually happened?
 *   Audit & Reports             — can I prove it?
 *   Administration              — who runs this?
 *
 * The two overview screens stay ungrouped deliberately: they are where people
 * land and where a demo starts, and putting them behind a fold costs a click on
 * the most-used item in the product to buy symmetry nobody asked for.
 *
 * Pages sit in the group matching the QUESTION they answer, not the subsystem
 * they are implemented by — which is why the two endpoint screens are split.
 * "Endpoint Agents" is the fleet you deploy and enforce with; "Endpoints" is
 * violations grouped by device, which is a monitoring view that happens to be
 * keyed on the same objects.
 */
const NAV_ITEMS: NavEntry[] = [
  // Where do I stand?
  { to: '/dashboard', labelKey: 'nav.items.dashboard', icon: 'dashboard', permission: 'view:dashboard' },
  { to: '/command-center', labelKey: 'nav.items.commandCenter', icon: 'signal', permission: 'view:violations' },

  // What AI is out there? — inventory, sanctioned and not.
  {
    labelKey: 'nav.groups.discover',
    icon: 'search',
    children: [
      { to: '/discovery', labelKey: 'nav.items.discovery', icon: 'search', permission: 'view:mcp-servers' },
      { to: '/agents', labelKey: 'nav.items.agents', icon: 'users', permission: 'view:agents' },
      { to: '/mcp-servers', labelKey: 'nav.items.mcpServers', icon: 'server', permission: 'view:mcp-servers' },
      { to: '/mcp-gateway', labelKey: 'nav.items.mcpGateway', icon: 'shield', permission: 'view:mcp-servers' },
      { to: '/mcp-flow', labelKey: 'nav.items.liveFlow', icon: 'bolt', permission: 'view:mcp-servers' },
      { to: '/provider-catalog', labelKey: 'nav.items.providerCatalog', icon: 'server', permission: 'view:policies' },
    ],
  },

  // What is allowed? — every "may X do Y" decision in the product.
  {
    labelKey: 'nav.groups.accessControl',
    icon: 'lock',
    children: [
      { to: '/policies', labelKey: 'nav.items.agentPolicies', icon: 'shield', permission: 'view:policies' },
      { to: '/llm-access-policy', labelKey: 'nav.items.llmAccessPolicy', icon: 'shield', permission: 'view:policies' },
      { to: '/watermark', labelKey: 'nav.items.watermark', icon: 'shield', permission: 'view:policies' },
      { to: '/jit-access', labelKey: 'nav.items.jit', icon: 'clock', permission: 'view:policies' },
      { to: '/ai-app-policy', labelKey: 'nav.items.aiAppPolicy', icon: 'chip', permission: 'view:policies' },
      { to: '/dlp-policy', labelKey: 'nav.items.dlpPolicy', icon: 'lock', permission: 'view:policies' },
    ],
  },

  // How do agents work together safely? — topics, chains of authority, and the proof trail.
  {
    labelKey: 'nav.groups.a2a',
    icon: 'share',
    children: [
      { to: '/a2a/topics', labelKey: 'nav.items.a2aTopics', icon: 'share', permission: 'view:agents' },
      { to: '/a2a/delegations', labelKey: 'nav.items.a2aDelegations', icon: 'key', permission: 'view:agents' },
      { to: '/a2a/provenance', labelKey: 'nav.items.a2aProvenance', icon: 'logs', permission: 'view:audit-logs' },
      { to: '/a2a/demo', labelKey: 'nav.items.a2aDemo', icon: 'bolt', permission: 'view:agents' },
    ],
  },

  // How is it stopped? — the three places policy actually lands.
  {
    labelKey: 'nav.groups.enforcement',
    icon: 'shield',
    children: [
      { to: '/ai-gateway', labelKey: 'nav.items.aiGateway', icon: 'bolt', permission: 'view:mcp-servers' },
      { to: '/endpoint-agents', labelKey: 'nav.items.endpointAgents', icon: 'chip', permission: 'view:agents' },
      { to: '/tripwire', labelKey: 'nav.items.tripwire', icon: 'warning', permission: 'view:violations' },
    ],
  },

  // What actually happened?
  {
    labelKey: 'nav.groups.monitoring',
    icon: 'view',
    children: [
      { to: '/violations', labelKey: 'nav.items.violations', icon: 'warning', permission: 'view:violations' },
      { to: '/dlp-activity', labelKey: 'nav.items.dlpActivity', icon: 'view', permission: 'view:violations' },
      { to: '/endpoints', labelKey: 'nav.items.endpoints', icon: 'signal', permission: 'view:violations' },
      { to: '/behaviour', labelKey: 'nav.items.behaviour', icon: 'signal', permission: 'view:violations' },
      { to: '/ai-cost', labelKey: 'nav.items.aiCost', icon: 'bolt', permission: 'view:dashboard' },
    ],
  },

  // Can I prove it?
  {
    labelKey: 'nav.groups.auditReports',
    icon: 'clipboardList',
    children: [
      { to: '/audit-logs', labelKey: 'nav.items.auditLogs', icon: 'logs', permission: 'view:audit-logs' },
      { to: '/reports', labelKey: 'nav.items.reports', icon: 'clipboardList', permission: 'view:dashboard' },
    ],
  },

  // Who runs this?
  {
    labelKey: 'nav.groups.administration',
    icon: 'settings',
    children: [
      { to: '/approvals', labelKey: 'nav.items.approvals', icon: 'checkBadge', permission: 'view:dashboard' },
      { to: '/users', labelKey: 'nav.items.users', icon: 'users', permission: 'manage:users' },
      { to: '/directory', labelKey: 'nav.items.activeDirectory', icon: 'server', permission: 'manage:settings' },
      { to: '/netskope', labelKey: 'nav.items.netskope', icon: 'server', permission: 'manage:settings' },
      { to: '/api-clients', labelKey: 'nav.items.apiClients', icon: 'key', permission: 'manage:settings' },
      { to: '/license', labelKey: 'nav.items.license', icon: 'key', permission: 'manage:settings' },
      { to: '/settings', labelKey: 'nav.items.settings', icon: 'settings', permission: 'manage:settings' },
      { to: '/guided-tests', labelKey: 'nav.items.guidedTests', icon: 'checkCircle', pocOnly: true },
    ],
  },
];

const Sidebar: React.FC<SidebarProps> = ({
  isConnected,
  isDarkMode,
  onToggleDarkMode,
  onLogout,
  collapsed = false,
  onToggleCollapse,
  showCollapseToggle = true,
  mobileOpen = false,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission, userAttributes, apiUrl } = useAuth();
  const [showChangePassword, setShowChangePassword] = React.useState(false);
  // Explicit open/closed overrides per group (keyed by label). A group with no
  // entry here falls back to auto-open when one of its children is the active route.
  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>({});
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);
  const isGroupActive = (group: NavGroupItem) => group.children.some((c) => isActive(c.to));
  const isGroupOpen = (group: NavGroupItem) => openGroups[group.labelKey] ?? isGroupActive(group);
  const toggleGroup = (group: NavGroupItem) =>
    setOpenGroups((prev) => ({ ...prev, [group.labelKey]: !isGroupOpen(group) }));

  // Screen search + pinning — lets an operator jump straight to a screen by
  // partial name instead of hunting through the grouped tree, and keep their
  // most-used screens above the fold. Pinned state is a per-browser UI
  // preference (same convention as sidebar-collapsed), not shared data, so it
  // lives in localStorage rather than a backend call.
  const [query, setQuery] = React.useState('');
  const [pinned, setPinned] = React.useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('ztai-pinned-screens');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const togglePin = (to: string) => {
    setPinned((prev) => {
      const next = prev.includes(to) ? prev.filter((p) => p !== to) : [...prev, to];
      try {
        localStorage.setItem('ztai-pinned-screens', JSON.stringify(next));
      } catch {
        // Non-critical: pin just won't survive a reload this session.
      }
      return next;
    });
  };

  const isItemVisible = (item: NavLinkItem) =>
    !(item.pocOnly && process.env.REACT_APP_POC_MODE !== 'true') &&
    !(item.permission && userAttributes && !hasPermission(item.permission));

  // Every leaf screen, flattened out of its group, for search + pin lookup.
  const allScreens: Array<{ item: NavLinkItem; group?: string }> = [];
  NAV_ITEMS.forEach((entry) => {
    if (isNavGroup(entry)) {
      entry.children.filter(isItemVisible).forEach((c) => allScreens.push({ item: c, group: entry.labelKey }));
    } else if (isItemVisible(entry)) {
      allScreens.push({ item: entry });
    }
  });

  const trimmedQuery = query.trim().toLowerCase();
  const searchResults = trimmedQuery
    ? allScreens.filter((s) => t(s.item.labelKey).toLowerCase().includes(trimmedQuery))
    : [];
  const pinnedScreens = allScreens.filter((s) => pinned.includes(s.item.to));

  // Shared link renderer for both top-level items and group children.
  // `groupLabel` (search results) shows which section a match lives in;
  // `showPin` (off for group headers' children when collapsed) toggles the
  // star affordance.
  const renderNavLink = (item: NavLinkItem, opts?: { groupLabel?: string; showPin?: boolean }) => {
    const label = t(item.labelKey);
    const active = isActive(item.to);
    const isPinned = pinned.includes(item.to);
    const showPin = opts?.showPin !== false && !collapsed;
    return (
      <Link
        key={item.to}
        to={item.to}
        className={cn(
          "group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium no-underline",
          "transition-all duration-200 ease-out",
          collapsed ? "justify-center px-2" : "justify-between",
          active
            ? "bg-[#44536A]/10 text-[#44536A] dark:bg-[#44536A]/15 dark:text-primary-300 shadow-sm"
            : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800/60 dark:hover:text-zinc-200"
        )}
        aria-label={label}
        onClick={onNavigate}
      >
        {/* Active indicator bar */}
        {active && (
          <motion.div
            layoutId="sidebar-active"
            className="absolute start-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-e-full bg-primary-500"
            transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
          />
        )}
        <span className="flex items-center gap-3 min-w-0 flex-1">
          <span className={cn(
            "flex-shrink-0 w-5 h-5",
            active ? "text-primary-600 dark:text-primary-400" : "text-zinc-400 group-hover:text-zinc-600 dark:text-zinc-500 dark:group-hover:text-zinc-300"
          )}>
            <Icon name={item.icon} size={20} />
          </span>
          {!collapsed && (
            <span className="truncate min-w-0">
              {label}
              {opts?.groupLabel && (
                <span className="block text-[10px] font-normal text-zinc-400 dark:text-zinc-600 truncate">
                  {opts.groupLabel}
                </span>
              )}
            </span>
          )}
        </span>
        {showPin && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(item.to); }}
            className={cn(
              "flex-shrink-0 grid place-items-center w-6 h-6 rounded-md transition-opacity",
              isPinned
                ? "text-amber-500 opacity-100"
                : "text-zinc-400 opacity-0 group-hover:opacity-100 hover:text-amber-500"
            )}
            title={isPinned ? t('nav.unpin') : t('nav.pin')}
            aria-label={isPinned ? t('nav.unpinLabel', { label }) : t('nav.pinLabel', { label })}
          >
            <Icon name={isPinned ? "starSolid" : "star"} size={14} />
          </button>
        )}
        {collapsed && (
          <span className="absolute start-full ms-2 rounded-md bg-zinc-900 dark:bg-zinc-100 px-2.5 py-1.5 text-xs font-medium text-white dark:text-zinc-900 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity shadow-lg whitespace-nowrap z-[100]" role="tooltip">
            {label}
          </span>
        )}
      </Link>
    );
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
      navigate('/');
    }
  };

  return (
    <aside
      className={cn(
        "fixed start-0 top-0 z-40 flex h-screen flex-col border-e border-zinc-200/80 dark:border-zinc-800/80",
        "bg-white/80 backdrop-blur-xl dark:bg-zinc-950/90",
        "transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
        collapsed ? "w-[64px] px-2 py-4" : "w-[260px] px-4 py-4",
        mobileOpen && "translate-x-0 shadow-2xl",
        !mobileOpen && "max-[920px]:-translate-x-full rtl:max-[920px]:translate-x-full max-[920px]:shadow-none",
        "min-[921px]:translate-x-0"
      )}
    >
      {/* Subtle gradient overlay */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-primary-500/[0.03] via-transparent to-primary-500/[0.02] dark:from-primary-400/[0.05] dark:to-primary-400/[0.02]" />

      {/* Collapse toggle */}
      {showCollapseToggle && (
        <button
          className={cn(
            "absolute -end-3 top-7 z-50 flex h-6 w-6 items-center justify-center rounded-full",
            "border border-zinc-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-900",
            "text-zinc-500 hover:text-primary-500 hover:border-primary-300 dark:hover:border-primary-600",
            "transition-all duration-200",
            collapsed && "rotate-0 rtl:rotate-180",
            !collapsed && "rotate-180 rtl:rotate-0"
          )}
          onClick={onToggleCollapse}
          title={collapsed ? t('shell.expandSidebar') : t('shell.collapseSidebar')}
          aria-label={collapsed ? t('shell.expandSidebar') : t('shell.collapseSidebar')}
        >
          <Icon name="chevronRight" size={12} />
        </button>
      )}

      {/* Logo section */}
      <div className="relative z-10 mb-3">
        <div className={cn(
          "flex items-center justify-center gap-3 pb-3 mb-3 border-b border-zinc-200/80 dark:border-zinc-800/60"
        )}>
          <div className={cn(
            "relative flex-shrink-0 overflow-hidden",
            collapsed ? "w-9 h-9 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 grid place-items-center" : "w-[110px] h-[36px]"
          )}>
            <img
              src={isDarkMode ? fidarDark : fidarLight}
              alt={t('shell.logoAlt')}
              className={cn(
                collapsed
                  ? "w-full h-full object-contain"
                  : "absolute h-[280%] w-auto top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
              )}
            />
          </div>
        </div>
      </div>


      {/* Navigation */}
      <nav className="sidebar-nav relative z-10 flex-1 overflow-y-auto">
        {!collapsed && (
          <div className="relative mb-3 px-0.5">
            <span className="pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-600">
              <Icon name="search" size={14} />
            </span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('nav.searchPlaceholder')}
              aria-label={t('nav.searchAria')}
              className="w-full rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900/60 py-1.5 ps-8 pe-7 text-xs text-zinc-700 dark:text-zinc-300 placeholder:text-zinc-400 dark:placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute end-1.5 top-1/2 -translate-y-1/2 grid place-items-center w-5 h-5 rounded text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                aria-label={t('nav.clearSearch')}
              >
                <Icon name="close" size={12} />
              </button>
            )}
          </div>
        )}

        {!collapsed && trimmedQuery ? (
          // Search mode: flat, ungrouped matches — partial, case-insensitive.
          <div className="flex flex-col gap-0">
            {searchResults.length > 0 && (
              <p className="px-3 pb-1 text-[10px] text-zinc-400 dark:text-zinc-600">
                {t('nav.resultCount', { count: searchResults.length })}
              </p>
            )}
            {searchResults.length === 0 ? (
              <p className="px-3 py-2 text-xs text-zinc-400 dark:text-zinc-600">
                {t('nav.noMatch', { query: query.trim() })}
              </p>
            ) : (
              searchResults.map(({ item, group }) => renderNavLink(item, { groupLabel: t(group) }))
            )}
          </div>
        ) : (
          <>
            {!collapsed && pinnedScreens.length > 0 && (
              <>
                <span className="block px-3 mb-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-400 dark:text-zinc-600">
                  {t('nav.pinned')}
                </span>
                <div className="flex flex-col gap-0 mb-3">
                  {pinnedScreens.map(({ item }) => renderNavLink(item))}
                </div>
              </>
            )}
            {!collapsed && (
              <span className="block px-3 mb-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-400 dark:text-zinc-600">
                {t('nav.navigation')}
              </span>
            )}
            <div className="flex flex-col gap-0">
          {NAV_ITEMS.map((item) => {
            if (isNavGroup(item)) {
              // Apply the SAME two filters as the flat-item path below —
              // permission and pocOnly — and drop the whole group if nothing
              // survives. Only permission was checked here before, which was
              // harmless while every pocOnly item happened to be top-level and
              // silently ships POC-only screens to production the moment one
              // moves into a group.
              const children = item.children.filter(
                (c) =>
                  !(c.pocOnly && process.env.REACT_APP_POC_MODE !== 'true') &&
                  !(c.permission && userAttributes && !hasPermission(c.permission))
              );
              if (children.length === 0) return null;

              // Icon-only (collapsed) sidebar: flatten the group into plain
              // top-level icon links rather than nesting a fold inside a fold.
              if (collapsed) {
                return children.map((c) => renderNavLink(c));
              }

              const open = isGroupOpen(item);
              const active = isGroupActive(item);
              return (
                <div key={item.labelKey}>
                  <button
                    type="button"
                    className={cn(
                      "group relative flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium",
                      "transition-all duration-200 ease-out",
                      active
                        ? "text-[#44536A] dark:text-primary-300"
                        : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800/60 dark:hover:text-zinc-200"
                    )}
                    onClick={() => toggleGroup(item)}
                    aria-expanded={open}
                  >
                    <span className={cn(
                      "flex-shrink-0 w-5 h-5",
                      active ? "text-primary-600 dark:text-primary-400" : "text-zinc-400 group-hover:text-zinc-600 dark:text-zinc-500 dark:group-hover:text-zinc-300"
                    )}>
                      <Icon name={item.icon} size={20} />
                    </span>
                    <span className="truncate flex-1 text-start">{t(item.labelKey)}</span>
                    <span className={cn("flex-shrink-0 w-4 h-4 transition-transform duration-200", open ? "rotate-90" : "rtl:rotate-180")}>
                      <Icon name="chevronRight" size={14} />
                    </span>
                  </button>
                  {open && (
                    <div className="flex flex-col gap-0 ps-4">
                      {children.map((c) => renderNavLink(c))}
                    </div>
                  )}
                </div>
              );
            }

            // Filter POC-only items when not in POC mode
            if (item.pocOnly && process.env.REACT_APP_POC_MODE !== 'true') {
              return null;
            }
            // Filter items the user lacks permission to see. When user context
            // hasn't loaded yet (userAttributes null), show everything to avoid
            // a flash of empty nav; the route guard still enforces access.
            if (item.permission && userAttributes && !hasPermission(item.permission)) {
              return null;
            }
            return renderNavLink(item);
          })}
            </div>
          </>
        )}
      </nav>

      {/* Bottom section */}
      <div className="relative z-10 mt-auto pt-3 border-t border-zinc-200/80 dark:border-zinc-800/60 flex flex-col gap-1">
        {/* User profile card */}
        {userAttributes && (
          <div
            className={cn(
              "flex items-center gap-3 rounded-lg px-2 py-2 mb-1",
              "bg-zinc-50 dark:bg-zinc-900/60",
              collapsed && "justify-center px-0 bg-transparent dark:bg-transparent"
            )}
          >
            <UserAvatar
              pictureUrl={userAttributes.profile?.pictureUrl}
              displayName={userAttributes.profile?.displayName || userAttributes.userId}
              email={userAttributes.profile?.email}
              phone={userAttributes.profile?.phone}
              role={userAttributes.role}
              size={collapsed ? 'sm' : 'md'}
              clickable={!collapsed}
              onShowProfile={() => navigate('/settings')}
            />
            {!collapsed && (
              <div className="flex flex-col min-w-0 flex-1">
                <span className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
                  {userAttributes.profile?.displayName || userAttributes.userId}
                </span>
                <span className="truncate text-[11px] text-zinc-400 dark:text-zinc-500">
                  {userAttributes.role}
                </span>
              </div>
            )}
            {!collapsed && (
              <button
                className="flex-shrink-0 grid place-items-center w-7 h-7 rounded-md text-zinc-400 hover:text-primary-500 hover:bg-zinc-100 dark:hover:bg-zinc-800/60 transition-colors"
                onClick={() => setShowChangePassword(true)}
                title={t('shell.changePassword')}
                aria-label={t('shell.changePassword')}
              >
                <Icon name="key" size={15} />
              </button>
            )}
          </div>
        )}

        {showChangePassword && (
          <ChangePasswordModal
            apiUrl={apiUrl}
            onClose={() => setShowChangePassword(false)}
            onSuccess={() => setShowChangePassword(false)}
          />
        )}

        {!collapsed && (
          <LanguageSwitcher className="mx-3 mb-1 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900/60 px-2 py-1.5 text-xs text-zinc-700 dark:text-zinc-300 focus:outline-none focus:ring-1 focus:ring-primary-400" />
        )}

        <button
          className={cn(
            "group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium w-full text-start",
            "text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800/60",
            "transition-all duration-200",
            collapsed && "justify-center px-2"
          )}
          onClick={onToggleDarkMode}
          aria-label={isDarkMode ? t('shell.switchToLight') : t('shell.switchToDark')}
        >
          <span className="flex-shrink-0 w-5 h-5 text-zinc-400 dark:text-zinc-500">
            <Icon name={isDarkMode ? 'moon' : 'sun'} size={20} />
          </span>
          {!collapsed && <span>{isDarkMode ? t('shell.lightMode') : t('shell.darkMode')}</span>}
          {collapsed && (
            <span className="absolute start-full ms-2 rounded-md bg-zinc-900 dark:bg-zinc-100 px-2.5 py-1.5 text-xs font-medium text-white dark:text-zinc-900 opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity shadow-lg whitespace-nowrap z-[100]" role="tooltip">
              {isDarkMode ? t('shell.switchToLight') : t('shell.switchToDark')}
            </span>
          )}
        </button>

        <button
          className={cn(
            "group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium w-full text-start",
            "text-red-600/80 hover:bg-red-50 hover:text-red-700 dark:text-red-400/80 dark:hover:bg-red-500/10 dark:hover:text-red-300",
            "transition-all duration-200",
            collapsed && "justify-center px-2"
          )}
          onClick={handleLogout}
          aria-label={t('shell.signOut')}
        >
          <span className="flex-shrink-0 w-5 h-5">
            <Icon name="power" size={20} />
          </span>
          {!collapsed && <span>{t('shell.signOut')}</span>}
          {collapsed && (
            <span className="absolute start-full ms-2 rounded-md bg-zinc-900 dark:bg-zinc-100 px-2.5 py-1.5 text-xs font-medium text-white dark:text-zinc-900 opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity shadow-lg whitespace-nowrap z-[100]" role="tooltip">
              {t('shell.signOut')}
            </span>
          )}
        </button>

        <AnimatePresence>
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="sidebar-version px-3 pt-2 text-[10px] text-zinc-400 dark:text-zinc-600"
            >
              {t('shell.platform')}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </aside>
  );
};

export default Sidebar;
